/**
 * StepsPipelinePanel
 *
 * Renders 5 individually triggerable pipeline step cards matching the
 * OCEL_Generierung_Ethen notebook chapters:
 *   Step 1 (extract)       → Chapter 0
 *   Step 2 (build_objects) → Chapter 1
 *   Step 3 (build_events)  → Chapter 2
 *   Step 4 (merge_map)     → Chapter 3.1–3.3
 *   Step 5 (assemble)      → Chapter 3.4–3.5
 *
 * Each card shows: status badge, last-run time, output files with row counts,
 * dependency warnings, and a Run button.
 */

import { useState } from 'react';
import { createCustomerETLApi } from '../api/etlServiceApi';

// ── Static step definitions ──────────────────────────────────────────────────

const STEPS = [
  {
    id: 'extract',
    num: '1',
    title: 'Extract',
    notebook: 'Ch. 0',
    color: 'bg-blue-500',
    ring: 'ring-blue-200',
    desc: 'Pull data from SQL Server ERP into the DuckDB warehouse via dlt.',
    dependsOn: [],
  },
  {
    id: 'build_objects',
    num: '2',
    title: 'Build Objects',
    notebook: 'Ch. 1',
    color: 'bg-indigo-500',
    ring: 'ring-indigo-200',
    desc: 'Build production_order and invoice OCEL objects from the produktionsauftraege warehouse query.',
    dependsOn: ['extract'],
  },
  {
    id: 'build_events',
    num: '3',
    title: 'Build Events',
    notebook: 'Ch. 2',
    color: 'bg-violet-500',
    ring: 'ring-violet-200',
    desc: 'Build all event types (cycle_start/end, P1–P3, productionorder_*) from warehouse queries.',
    dependsOn: ['extract', 'build_objects'],
  },
  {
    id: 'merge_map',
    num: '4',
    title: 'Merge & Map',
    notebook: 'Ch. 3.1–3.3',
    color: 'bg-amber-500',
    ring: 'ring-amber-200',
    desc: 'Merge object and event intermediates, remove orphan events, build event-to-object relationship mapping.',
    dependsOn: ['build_objects', 'build_events'],
    csvDownload: true,
  },
  {
    id: 'assemble',
    num: '5',
    title: 'Assemble & Export',
    notebook: 'Ch. 3.4–3.5',
    color: 'bg-emerald-500',
    ring: 'ring-emerald-200',
    desc: 'Assemble OCEL 2.0 JSON from the merged intermediates and write to the output directory.',
    dependsOn: ['merge_map'],
  },
];

// ── Helpers ──────────────────────────────────────────────────────────────────

function formatBytes(bytes) {
  if (!bytes) return '—';
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${(bytes / 1024).toFixed(1)} KB`;
  return `${(bytes / 1024 / 1024).toFixed(1)} MB`;
}

function formatRelative(isoStr) {
  if (!isoStr) return null;
  const diff = Date.now() - new Date(isoStr).getTime();
  if (diff < 60_000) return 'just now';
  if (diff < 3_600_000) return `${Math.floor(diff / 60_000)}m ago`;
  if (diff < 86_400_000) return `${Math.floor(diff / 3_600_000)}h ago`;
  return `${Math.floor(diff / 86_400_000)}d ago`;
}

function formatRows(n) {
  if (n == null) return null;
  return n >= 1000 ? `${(n / 1000).toFixed(1)}k rows` : `${n} rows`;
}

// ── Sub-components ───────────────────────────────────────────────────────────

function StatusBadge({ status }) {
  if (!status || status === 'pending') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-gray-100 text-gray-500">
        Idle
      </span>
    );
  }
  if (status === 'running') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-blue-50 text-blue-700">
        <span className="w-2 h-2 border border-blue-500 border-t-transparent rounded-full animate-spin" />
        Running
      </span>
    );
  }
  if (status === 'done') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-emerald-50 text-emerald-700">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="m4.5 12.75 6 6 9-13.5" />
        </svg>
        Done
      </span>
    );
  }
  if (status === 'failed') {
    return (
      <span className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[10px] font-medium bg-red-50 text-red-700">
        <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
          <path strokeLinecap="round" strokeLinejoin="round" d="M6 18 18 6M6 6l12 12" />
        </svg>
        Failed
      </span>
    );
  }
  return null;
}

function OutputFiles({ files, stepId }) {
  const [open, setOpen] = useState(false);
  if (!files || files.length === 0) return null;

  return (
    <div className="mt-2">
      <button
        onClick={() => setOpen(v => !v)}
        className="text-[11px] text-gray-400 hover:text-gray-600 flex items-center gap-1 transition-colors"
      >
        <svg
          className={`w-3 h-3 transition-transform ${open ? 'rotate-90' : ''}`}
          fill="none"
          viewBox="0 0 24 24"
          stroke="currentColor"
          strokeWidth={2.5}
        >
          <path strokeLinecap="round" strokeLinejoin="round" d="m8.25 4.5 7.5 7.5-7.5 7.5" />
        </svg>
        {files.length} output {files.length === 1 ? 'file' : 'files'}
      </button>
      {open && (
        <ul className="mt-1.5 space-y-1">
          {files.map((f, i) => (
            <li key={i} className="flex items-center justify-between bg-gray-50 rounded px-2 py-1">
              <span className="text-[11px] text-gray-600 font-mono truncate max-w-[55%]">
                {f.path.split('/').pop()}
              </span>
              <div className="flex items-center gap-2 text-[10px] text-gray-400 flex-shrink-0">
                {formatRows(f.rows) && (
                  <span className="bg-gray-100 rounded px-1">{formatRows(f.rows)}</span>
                )}
                <span>{formatBytes(f.size_bytes)}</span>
              </div>
            </li>
          ))}
        </ul>
      )}
    </div>
  );
}

function DownloadCsvButton({ stepId, customerId, disabled }) {
  const [downloading, setDownloading] = useState(false);
  const [downloadError, setDownloadError] = useState(null);

  const handleDownload = async () => {
    if (!customerId) return;
    setDownloadError(null);
    setDownloading(true);
    try {
      const api = createCustomerETLApi(customerId);
      const blob = await api.downloadStepCsv(stepId);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `${stepId}_csv_export.zip`;
      document.body.appendChild(a);
      a.click();
      a.remove();
      URL.revokeObjectURL(url);
    } catch (err) {
      setDownloadError(err?.response?.data?.detail ?? err.message ?? 'Download failed');
    } finally {
      setDownloading(false);
    }
  };

  return (
    <div className="flex flex-col items-end gap-1">
      <button
        onClick={handleDownload}
        disabled={disabled || downloading || !customerId}
        title={disabled ? 'Run this step first' : 'Download object, event, mapping and relationship tables as CSV'}
        className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors border
          disabled:bg-gray-50 disabled:text-gray-300 disabled:cursor-not-allowed disabled:border-gray-100
          bg-white hover:bg-amber-50 text-amber-700 border-amber-200"
      >
        {downloading ? (
          <span className="w-3 h-3 border border-amber-400/60 border-t-amber-700 rounded-full animate-spin" />
        ) : (
          <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
            <path strokeLinecap="round" strokeLinejoin="round" d="M3 16.5v2.25A2.25 2.25 0 0 0 5.25 21h13.5A2.25 2.25 0 0 0 21 18.75V16.5M16.5 12 12 16.5m0 0L7.5 12m4.5 4.5V3" />
          </svg>
        )}
        CSV
      </button>
      {downloadError && (
        <span className="text-[10px] text-red-600 max-w-[160px] text-right">{downloadError}</span>
      )}
    </div>
  );
}

function StepCard({ stepDef, serverStatus, etlServiceReachable, onRun, customerId }) {
  const [running, setRunning] = useState(false);
  const [runError, setRunError] = useState(null);

  const status = serverStatus?.status ?? 'pending';
  const depMet = serverStatus?.dependency_met ?? stepDef.dependsOn.length === 0;
  const isRunning = status === 'running' || running;

  const disabledReason = !etlServiceReachable
    ? 'ETL service unreachable'
    : isRunning
      ? 'Step is running'
      : !depMet
        ? `Requires: ${stepDef.dependsOn.map(d => STEPS.find(s => s.id === d)?.title ?? d).join(' + ')}`
        : null;

  const handleRun = async () => {
    setRunError(null);
    setRunning(true);
    try {
      await onRun(stepDef.id);
    } catch (err) {
      setRunError(err?.response?.data?.detail ?? err.message ?? 'Failed to start step');
    } finally {
      setRunning(false);
    }
  };

  return (
    <div
      className={`bg-white rounded-xl border shadow-sm overflow-hidden transition-all ${
        status === 'failed'
          ? 'border-red-200'
          : status === 'running'
            ? `border-blue-200 ${stepDef.ring} ring-1`
            : 'border-gray-200'
      }`}
    >
      <div className="p-4">
        {/* Header row */}
        <div className="flex items-start justify-between gap-3">
          <div className="flex items-start gap-3 min-w-0">
            {/* Colored circle */}
            <div
              className={`w-7 h-7 rounded-full ${stepDef.color} flex items-center justify-center flex-shrink-0 mt-0.5`}
            >
              <span className="text-white text-[11px] font-bold">{stepDef.num}</span>
            </div>

            <div className="min-w-0">
              <div className="flex items-center gap-2 flex-wrap">
                <span className="text-sm font-semibold text-gray-800">{stepDef.title}</span>
                <span className="text-[10px] text-gray-400 bg-gray-100 px-1.5 py-0.5 rounded">
                  {stepDef.notebook}
                </span>
                <StatusBadge status={status} />
              </div>
              <p className="text-xs text-gray-400 mt-1 leading-relaxed">{stepDef.desc}</p>
            </div>
          </div>

          {/* Run + download buttons */}
          <div className="flex-shrink-0 flex items-start gap-2">
            {stepDef.csvDownload && (
              <DownloadCsvButton stepId={stepDef.id} customerId={customerId} disabled={status !== 'done'} />
            )}
            <button
              onClick={handleRun}
              disabled={!!disabledReason}
              title={disabledReason ?? `Run ${stepDef.title}`}
              className="flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-xs font-medium transition-colors
                disabled:bg-gray-100 disabled:text-gray-400 disabled:cursor-not-allowed
                bg-primary-600 hover:bg-primary-700 text-white"
            >
              {isRunning ? (
                <>
                  <span className="w-3 h-3 border border-white/40 border-t-white rounded-full animate-spin" />
                  Running
                </>
              ) : (
                <>
                  <svg className="w-3 h-3" fill="none" viewBox="0 0 24 24" stroke="currentColor" strokeWidth={2.5}>
                    <path strokeLinecap="round" strokeLinejoin="round" d="M5.25 5.653c0-.856.917-1.398 1.667-.986l11.54 6.347a1.125 1.125 0 0 1 0 1.972l-11.54 6.347a1.125 1.125 0 0 1-1.667-.986V5.653Z" />
                  </svg>
                  Run
                </>
              )}
            </button>
          </div>
        </div>

        {/* Meta row */}
        <div className="mt-3 flex items-center gap-3 flex-wrap">
          {serverStatus?.finished_at && (
            <span className="text-[11px] text-gray-400">
              Last run: {formatRelative(serverStatus.finished_at)}
            </span>
          )}
          {!depMet && (
            <span className="text-[11px] text-amber-600 bg-amber-50 px-2 py-0.5 rounded-full">
              Requires: {stepDef.dependsOn.map(d => STEPS.find(s => s.id === d)?.title ?? d).join(' + ')}
            </span>
          )}
        </div>

        {/* Output files */}
        {serverStatus?.output_files?.length > 0 && (
          <OutputFiles files={serverStatus.output_files} stepId={stepDef.id} />
        )}

        {/* Error */}
        {status === 'failed' && serverStatus?.error && (
          <p className="mt-2 text-[11px] text-red-600 bg-red-50 rounded px-2 py-1 break-words">
            {serverStatus.error}
          </p>
        )}

        {/* Run error */}
        {runError && (
          <p className="mt-2 text-[11px] text-red-600 bg-red-50 rounded px-2 py-1 break-words">
            {runError}
          </p>
        )}
      </div>
    </div>
  );
}

// ── Main component ────────────────────────────────────────────────────────────

/**
 * Renders the 5 step cards. Accepts shared pipeline state as props so that the
 * parent (PipelinePanel) can also pass the same state to StepDebugPanel.
 */
export default function StepsPipelinePanel({ etlServiceReachable, steps, loading, error, runStep, customerId }) {
  const getServerStatus = (stepId) =>
    steps.find((s) => s.step_id === stepId) ?? null;

  if (!etlServiceReachable) {
    return (
      <div className="text-center py-10 text-gray-400 text-sm">
        ETL service is not reachable — step controls are disabled.
      </div>
    );
  }

  if (loading) {
    return (
      <div className="space-y-3">
        {[1, 2, 3, 4, 5].map(i => (
          <div key={i} className="h-24 bg-white/60 rounded-xl border border-gray-100 animate-pulse" />
        ))}
      </div>
    );
  }

  if (error) {
    return (
      <div className="bg-red-50 border border-red-200 rounded-xl p-4 text-sm text-red-700">
        {error}
      </div>
    );
  }

  return (
    <div className="space-y-3">
      {STEPS.map((stepDef) => (
        <StepCard
          key={stepDef.id}
          stepDef={stepDef}
          serverStatus={getServerStatus(stepDef.id)}
          etlServiceReachable={etlServiceReachable}
          onRun={runStep}
          customerId={customerId}
        />
      ))}
    </div>
  );
}
